import fs from "fs";
import path from "path";
import { OrchestratorConfig } from "./configTypes.js";

const CONFIG_DIR_NEW = ".kyro";
const CONFIG_DIR_LEGACY = ".rn-orchestrator";
const CONFIG_FILE = "config.json";

export const DEFAULT_CONFIG: OrchestratorConfig = {
  scoreThreshold: 70,
  maxWarnings: 20,
  failOnError: true,
};

/**
 * Loads `.kyro/config.json` from `projectRoot`.
 *
 * Gracefully falls back to `.rn-orchestrator/config.json` and displays a warning,
 * or falls back to DEFAULT_CONFIG if both are absent.
 */
export function loadConfig(projectRoot: string): OrchestratorConfig {
  const newConfigPath = path.join(projectRoot, CONFIG_DIR_NEW, CONFIG_FILE);
  const legacyConfigPath = path.join(projectRoot, CONFIG_DIR_LEGACY, CONFIG_FILE);

  let configPath = newConfigPath;
  let hasLegacy = false;

  if (fs.existsSync(newConfigPath)) {
    configPath = newConfigPath;
  } else if (fs.existsSync(legacyConfigPath)) {
    configPath = legacyConfigPath;
    hasLegacy = true;
  } else {
    return { ...DEFAULT_CONFIG };
  }

  if (hasLegacy) {
    console.warn("Legacy rn-orchestrator configuration detected. Please migrate to .kyro/config.json");
  }

  try {
    const raw = fs.readFileSync(configPath, "utf-8");
    const parsed = JSON.parse(raw) as Partial<OrchestratorConfig>;

    return {
      scoreThreshold:
        typeof parsed.scoreThreshold === "number"
          ? parsed.scoreThreshold
          : DEFAULT_CONFIG.scoreThreshold,
      maxWarnings:
        typeof parsed.maxWarnings === "number"
          ? parsed.maxWarnings
          : DEFAULT_CONFIG.maxWarnings,
      failOnError:
        typeof parsed.failOnError === "boolean"
          ? parsed.failOnError
          : DEFAULT_CONFIG.failOnError,
      defaultAndroidDevice: parsed.defaultAndroidDevice,
      defaultIosSimulator: parsed.defaultIosSimulator,
    };
  } catch {
    // Invalid JSON — fall back silently
    return { ...DEFAULT_CONFIG };
  }
}

/**
 * Writes a config file to `.kyro/config.json`.
 * Creates the directory if it does not exist.
 */
export function writeDefaultConfig(projectRoot: string): void {
  const configDir = path.join(projectRoot, CONFIG_DIR_NEW);
  const configPath = path.join(configDir, CONFIG_FILE);

  if (!fs.existsSync(configDir)) {
    fs.mkdirSync(configDir, { recursive: true });
  }

  fs.writeFileSync(configPath, JSON.stringify(DEFAULT_CONFIG, null, 2), "utf-8");
}

/**
 * Merges updates into `.kyro/config.json`. Creates it if missing.
 */
export function saveConfig(projectRoot: string, configUpdates: Partial<OrchestratorConfig>): void {
  const configDir = path.join(projectRoot, CONFIG_DIR_NEW);
  const configPath = path.join(configDir, CONFIG_FILE);

  const currentConfig = loadConfig(projectRoot);
  const merged = { ...currentConfig, ...configUpdates };

  if (!fs.existsSync(configDir)) {
    fs.mkdirSync(configDir, { recursive: true });
  }

  fs.writeFileSync(configPath, JSON.stringify(merged, null, 2), "utf-8");
}
